import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, resolveImageUrl } from "@/lib/api";
import EmailPopup from "@/components/EmailPopup";
import SubscribeForm from "@/components/SubscribeForm";
import { trackViewContent, trackAmazonClick } from "@/lib/analytics";
import { trackPageviewServer, trackAmazonClickServer } from "@/lib/tracking";

function StarRow({ rating = 0, count = 0 }) {
  if (!rating && !count) return null;
  const full = Math.floor(rating);
  const half = rating - full >= 0.5;
  const stars = [];
  for (let i = 0; i < 5; i++) {
    let fill = "#E0E0E0";
    if (i < full || (i === full && half)) fill = "#FFB400";
    stars.push(
      <svg key={i} width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M12 2 L14.5 8.5 L21.5 9 L16 13.5 L17.5 20.5 L12 16.8 L6.5 20.5 L8 13.5 L2.5 9 L9.5 8.5 Z"
          fill={fill}
          stroke="#1E1E24"
          strokeWidth="1"
        />
      </svg>
    );
  }
  return (
    <div className="flex items-center justify-center gap-2" data-testid="product-rating">
      <div className="flex items-center gap-0.5">{stars}</div>
      {rating > 0 && <span className="font-bold text-[#1E1E24]">{rating.toFixed(1)}</span>}
      {count > 0 && (
        <span className="text-[#5E5E66] text-sm">· {count.toLocaleString()} reviews</span>
      )}
    </div>
  );
}

export default function ProductPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    window.scrollTo({ top: 0, behavior: "instant" });
    (async () => {
      try {
        const { data } = await api.get(`/products/${slug}`);
        setProduct(data);
        trackViewContent(data);
        trackPageviewServer(data.slug || slug);
      } catch (e) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFDF9] flex items-center justify-center">
        <p className="text-[#5E5E66]">Loading...</p>
      </div>
    );
  }

  if (notFound || !product) {
    return (
      <div className="min-h-screen bg-[#FFFDF9] flex items-center justify-center p-6">
        <div className="text-center max-w-md" data-testid="product-not-found">
          <div className="text-6xl mb-4">🦡</div>
          <h1 className="font-headings text-3xl font-bold mb-2">Product not found</h1>
          <p className="text-[#5E5E66] mb-6">This deal may have moved or been retired.</p>
          <Link
            to="/"
            className="bg-[#FF5A5F] text-white border-2 border-[#1E1E24] rounded-full font-bold tracking-wide px-8 py-3 inline-block"
          >
            Back to deals
          </Link>
        </div>
      </div>
    );
  }

  const imgSrc = resolveImageUrl(product.image_url);
  const editorLabel = product.editor_label || "Amazon editor's pick";
  const rating = product.rating || 0;
  const highlights = product.highlights || [];

  return (
    <div className="min-h-screen bg-[#FFFDF9] text-[#1E1E24]" data-testid="product-page">
      {/* Minimal header */}
      <header className="max-w-xl mx-auto flex items-center justify-between px-5 pt-4 md:pt-6 pb-2">
        <Link to="/" className="flex items-center gap-2" data-testid="product-brand">
          <span className="text-xl md:text-2xl">🦡</span>
          <span className="font-headings text-base md:text-lg font-bold tracking-tight">
            Jungle <span className="text-[#FF5A5F]">Badger</span>
          </span>
        </Link>
        <Link
          to="/"
          className="text-xs font-bold text-[#5E5E66] hover:text-[#1E1E24] transition-colors"
          data-testid="product-back-link"
        >
          ← All deals
        </Link>
      </header>

      <main className="max-w-xl mx-auto px-5 py-4 md:py-10">
        {/* Image (no shadow, subtle border) */}
        <div className="rounded-3xl overflow-hidden border border-[#1E1E24]/10 bg-[#F4F1EA] aspect-square mb-3 md:mb-6">
          {imgSrc ? (
            <img
              src={imgSrc}
              alt={product.name}
              className="w-full h-full object-cover"
              data-testid="product-hero-image"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-8xl">🦡</div>
          )}
        </div>

        {/* Title (directly under photo) */}
        <h1
          className="font-headings text-lg md:text-xl font-bold text-center leading-snug tracking-tight text-balance mx-auto max-w-md mb-4 md:mb-5"
          data-testid="product-name"
        >
          {product.name}
        </h1>

        {/* Primary CTA */}
        <a
          href={product.amazon_url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => { trackAmazonClick(product); trackAmazonClickServer(product.slug || slug); }}
          data-testid="product-buy-cta"
          className="block w-full bg-[#FF5A5F] text-white border-2 border-[#1E1E24] rounded-full hover:bg-[#1E1E24] transition-all font-bold tracking-wide px-8 py-3 md:py-4 text-center text-base md:text-lg mb-3"
        >
          {product.cta_text || "Open in Amazon App"}
        </a>

        {/* Trust row */}
        <p className="text-sm text-[#5E5E66] text-center mb-6 md:mb-8">
          Opens Amazon  ·  Free returns  ·  Secure checkout
        </p>

        {/* Rating (below trust row) */}
        {(rating > 0 || product.review_count > 0) && (
          <div className="mb-6 md:mb-8">
            <StarRow rating={rating} count={product.review_count || 0} />
          </div>
        )}

        {/* Description (closer copy at bottom) */}
        {product.description && product.description_enabled !== false && (
          <p
            className="text-sm md:text-base text-center text-[#5E5E66] leading-relaxed mb-10 md:mb-12 max-w-md mx-auto"
            data-testid="product-short-description"
          >
            {product.description}
          </p>
        )}

        {/* Divider text */}
        <div className="border-t border-[#1E1E24]/10 mb-8" />

        {/* Subscribe heading */}
        <h2 className="font-headings text-2xl md:text-3xl font-bold text-center leading-tight mb-6">
          Amazon deals straight to your inbox
        </h2>

        {/* Subscribe form + trust checkmarks */}
        <div className="mb-10">
          <SubscribeForm
            productContext={product.name || "product-page-inline"}
            testIdPrefix="inline-subscribe"
          />
        </div>
      </main>

      {product.popup_enabled !== false && (
        <EmailPopup context="product" productContext={product.name} />
      )}

      {/* Footer */}
      <footer className="border-t border-[#1E1E24]/10">
        <div className="max-w-xl mx-auto px-5 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#5E5E66]">
          <div className="flex items-center gap-2">
            <span className="text-lg" aria-hidden="true">🦡</span>
            <span className="font-headings font-bold text-[#1E1E24]">
              Jungle <span className="text-[#FF5A5F]">Badger</span> Deals
            </span>
          </div>
          <p>© {new Date().getFullYear()} Jungle Badger.</p>
        </div>
      </footer>
    </div>
  );
}
